import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { UserPlus, UserMinus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Empty, Panel } from "./_shared";
import { ExitChecklistDialog } from "./Separations";

export const DEFAULT_ONBOARDING_STEPS = [
  { key: "offer_signed",       label: "Offer & acceptance signed" },
  { key: "documents_received", label: "ID/passport, CV, qualifications, bank, NoK on file" },
  { key: "contract_filed",     label: "Contract created & filed" },
  { key: "account_setup",      label: "Email + system + ERP + payroll access" },
  { key: "equipment_issued",   label: "Laptop / phone / PPE issued (Asset Management)" },
  { key: "orientation_done",   label: "HR briefing + safety training completed" },
  { key: "probation_review",   label: "Probation end set; reviewed" },
];

function OnboardingChecklistDialog({ staff: s, trigger, onChanged }) {
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(s.onboarding_progress || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setProgress(s.onboarding_progress || {}); }, [open, s]);
  const toggle = async (key, done) => {
    setBusy(true);
    const next = { ...progress, [key]: done ? { done: true, at: new Date().toISOString() } : null };
    setProgress(next);
    try {
      await api.patch(`/hr/staff/${s.id}`, { onboarding_progress: next });
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const doneCount = DEFAULT_ONBOARDING_STEPS.filter((st) => progress[st.key]?.done).length;
  const pct = Math.round(doneCount / DEFAULT_ONBOARDING_STEPS.length * 100);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Onboarding · {s.full_name}</DialogTitle>
          <DialogDescription>{s.designation || "—"} · {s.department || "—"} · joined {formatDate(s.join_date)}</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center justify-between text-xs font-mono-alt uppercase tracking-wider text-slate-600 mb-1.5">
            <span>Onboarding progress</span>
            <span className="tabular-nums font-bold">{pct}% · {doneCount}/{DEFAULT_ONBOARDING_STEPS.length}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-white">
            <div className={`h-full ${pct === 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
        <ul className="space-y-1.5">
          {DEFAULT_ONBOARDING_STEPS.map((st) => {
            const done = !!progress[st.key]?.done;
            return (
              <li key={st.key}
                  className={`flex items-start gap-3 rounded-md border p-3 text-sm ${done ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-white"}`}>
                <input type="checkbox" checked={done} disabled={busy}
                       onChange={(e) => toggle(st.key, e.target.checked)}
                       className="mt-0.5 h-4 w-4 cursor-pointer accent-emerald-600"
                       data-testid={`onb-${s.id}-${st.key}`} />
                <div className="min-w-0 flex-1">
                  <div className={`font-medium ${done ? "text-slate-500 line-through" : "text-slate-900"}`}>{st.label}</div>
                  {done && progress[st.key]?.at && (
                    <div className="mt-0.5 text-[10px] text-slate-500">✓ {formatDate(progress[st.key].at)}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

export default function OnboardingGuide({ staff }) {
  const [seps, setSeps] = useState([]);
  useEffect(() => { api.get("/hr/separations").then(({ data }) => setSeps(data.items || [])); }, []);

  const ninetyDaysAgo = new Date(Date.now() - 90 * 86400000);
  const recentHires = staff.filter((s) => {
    if (!s.join_date) return false;
    const jd = new Date(s.join_date);
    if (isNaN(jd)) return false;
    const onbDone = Object.values(s.onboarding_progress || {}).filter((p) => p?.done).length;
    return jd >= ninetyDaysAgo || onbDone < DEFAULT_ONBOARDING_STEPS.length;
  }).slice(0, 30);

  const inProgressSeps = seps.filter((s) => (s.status || "in_progress") === "in_progress");

  const reloadStaff = () => { api.get("/hr/staff").catch(() => {}); };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Panel title={`Onboarding · ${recentHires.length} recent hire(s)`} icon={<UserPlus size={16} style={{ color: "#059669" }} />}>
        {recentHires.length === 0 ? (
          <Empty text="No recent hires needing onboarding." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {recentHires.map((s) => {
              const done = DEFAULT_ONBOARDING_STEPS.filter((st) => s.onboarding_progress?.[st.key]?.done).length;
              const pct = Math.round(done / DEFAULT_ONBOARDING_STEPS.length * 100);
              return (
                <li key={s.id} className="flex items-center gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">{s.full_name}</div>
                    <div className="text-xs text-slate-500 truncate">{s.designation || "—"} · joined {formatDate(s.join_date)}</div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="h-1.5 w-32 overflow-hidden rounded-full bg-slate-100">
                        <div className={`h-full ${pct === 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="font-mono-alt text-[10px] tabular-nums text-slate-500">{done}/{DEFAULT_ONBOARDING_STEPS.length}</span>
                    </div>
                  </div>
                  <OnboardingChecklistDialog staff={s} onChanged={reloadStaff}
                    trigger={
                      <Button size="sm" variant={pct === 100 ? "outline" : "default"}
                              className={pct < 100 ? "btn-primary-blue" : ""}
                              data-testid={`open-onb-${s.id}`}>
                        {pct === 100 ? "View" : "Tick steps"}
                      </Button>
                    } />
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <Panel title={`Offboarding · ${inProgressSeps.length} exit(s) in progress`} icon={<UserMinus size={16} style={{ color: "#dc2626" }} />}>
        {inProgressSeps.length === 0 ? (
          <Empty text="No exits currently in progress. Initiate one from the Separations tab." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {inProgressSeps.map((s) => {
              const name = staff.find((x) => x.id === s.staff_id)?.full_name || s.staff_id;
              const checklist = s.exit_checklist || [];
              const doneCnt = checklist.filter((i) => i.done).length;
              const pct = checklist.length ? Math.round(doneCnt / checklist.length * 100) : 0;
              return (
                <li key={s.id} className="flex items-center gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">{name}</div>
                    <div className="text-xs text-slate-500 truncate">{s.separation_type?.replace(/_/g, " ")} · effective {formatDate(s.effective_date)}</div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="h-1.5 w-32 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="font-mono-alt text-[10px] tabular-nums text-slate-500">{doneCnt}/{checklist.length}</span>
                    </div>
                  </div>
                  <ExitChecklistDialog separation={s} employeeName={name} onChanged={() => api.get("/hr/separations").then(({ data }) => setSeps(data.items || []))}
                    trigger={
                      <Button size="sm" className="btn-primary-blue" data-testid={`open-offb-${s.id}`}>
                        Open exit
                      </Button>
                    } />
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </div>
  );
}
